import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message?: string | null;
  type?: ToastType;
  onClose?: () => void;
  /** Auto-dismiss delay in ms (defaults to 3000) */
  duration?: number;
}

const toastStyles = {
  success: {
    bg: 'bg-green-100',
    border: 'border-green-400',
    text: 'text-green-800',
    icon: CheckCircle,
  },
  error: {
    bg: 'bg-red-100',
    border: 'border-red-400',
    text: 'text-red-800',
    icon: XCircle,
  },
  warning: {
    bg: 'bg-yellow-100',
    border: 'border-yellow-400',
    text: 'text-yellow-800',
    icon: AlertCircle,
  },
  info: {
    bg: 'bg-blue-100',
    border: 'border-blue-400',
    text: 'text-blue-800',
    icon: Info,
  },
};

export default function Toast({ type = 'info', message, onClose, duration }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (message) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, duration || 3000);

      return () => clearTimeout(timer);
    }
  }, [message, duration, onClose]);

  if (!message || !isVisible) {
    return null;
  }

  const style = toastStyles[type];
  const Icon = style.icon;

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 min-w-[320px] max-w-[90vw]">
      <div className={`${style.bg} ${style.border} border rounded-lg shadow-lg p-4`}>
        <div className="flex items-center">
          <Icon className={`h-5 w-5 mr-3 ${style.text}`} />
          <p className={`text-sm font-medium ${style.text}`}>{message}</p>
        </div>
      </div>
    </div>
  );
}